import { useMemo } from "react";
import { IdwTable } from "../../../common/idw-table/idw-table";
import { resources } from "@constants/hardcoded-strings";
import { IdwTableContainer } from "../../../common/idw-table-container/idw-table-container";
import { formatDateToString } from "@utils/date-util";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import {
    FormattedVerificationTransactionDetailsScore,
    FormattedVerificationTransactionDetailsTrustScoreBreakdown,
    FormattedVerificationTransactionDetailsTrustScoreInput,
    FormattedVerificationTransactionDetailsTrustScoreModifier,
    FormattedVerificationTransactionDetailsAssertionScoreBreakdown,
    FormattedVerificationTransactionDetailsAssertionScoreModifier
} from "@idw-types/data/verification-transaction-details";
import { VerificationTransactionScoreTypes } from "@enums/verification-transaction-score-types";
import { VerificationTransactionDetailsScoresModal } from "./verification-transaction-details-scores-modal/verification-transaction-details-scores-modal";
import Button from "react-bootstrap/Button";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import styles from "./scores-table-container.less";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";

type ScoresTableContainerProps = {
    scores: Array<FormattedVerificationTransactionDetailsScore>;
    trustScoreBreakdowns: Array<FormattedVerificationTransactionDetailsTrustScoreBreakdown>;
    trustScoreInputs: Array<FormattedVerificationTransactionDetailsTrustScoreInput>;
    trustScoreModifiers: Array<FormattedVerificationTransactionDetailsTrustScoreModifier>;
    assertionScoreBreakdowns: Array<FormattedVerificationTransactionDetailsAssertionScoreBreakdown>;
    assertionScoreModifiers: Array<FormattedVerificationTransactionDetailsAssertionScoreModifier>;
};

export const ScoresTableContainer = (props: ScoresTableContainerProps) => {
    const {
        scores,
        trustScoreBreakdowns,
        trustScoreInputs,
        trustScoreModifiers,
        assertionScoreBreakdowns,
        assertionScoreModifiers
    } = props;
    const modalContext = useModalContext();

    const scoreTableData = useMemo(() => {
        return scores.filter(score => score.scoreType === VerificationTransactionScoreTypes.IDW_RISK_SCORE).map(score => ({
            ...score,
            scoreType: VerificationTransactionScoreTypes.IDW_TRUST_SCORE
        }));
    }, [scores]);

    const onClickDetails = (score: FormattedVerificationTransactionDetailsScore) => {
        const relatedTrustScoreBreakdown = trustScoreBreakdowns.find(tsb => tsb.userAcquiredScoreHistoryID === score.id);

        const relatedTrustScoreInputs = relatedTrustScoreBreakdown ? trustScoreInputs.filter(tsi => {
            return tsi.trustScoreBreakdownID === relatedTrustScoreBreakdown.id && tsi.scoreWeight > 0;
        }) : [];
        const relatedTrustScoreModifiers = relatedTrustScoreBreakdown ? trustScoreModifiers.filter(tsm => {
            return tsm.trustScoreBreakdownID === relatedTrustScoreBreakdown.id && isStringTrue(tsm.wasApplied)
        }) : [];

        const renderVerificationTransactionDetailsScoresModal = (onModalClose: () => void) => {
            return (
                <VerificationTransactionDetailsScoresModal
                    onModalClose={onModalClose}
                    scores={scores}
                    trustScoreBreakdown={relatedTrustScoreBreakdown}
                    trustScoreInputs={relatedTrustScoreInputs}
                    trustScoreModifiers={relatedTrustScoreModifiers}
                    assertionScoreBreakdowns={assertionScoreBreakdowns}
                    assertionScoreModifiers={assertionScoreModifiers}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.VerificationTransactionDetailsScores,
            renderFunction: renderVerificationTransactionDetailsScoresModal
        });
    };

    const columnHelper = createColumnHelper<FormattedVerificationTransactionDetailsScore>();
    const columns = useMemo(() => ([
        columnHelper.accessor("date", {
            header: resources.tables.verificationTransactionDetailsScores.columns.date.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null
        }),
        columnHelper.accessor("scoreType", {
            header: resources.tables.verificationTransactionDetailsScores.columns.scoreType.header
        }),
        columnHelper.accessor("score", {
            header: resources.tables.verificationTransactionDetailsScores.columns.score.header
        }),
        columnHelper.display({
            id: "details",
            header: "",
            cell: (props) => (
                <div className={styles.detailsButtonContainer}>
                    <Button className={styles.detailsButton} variant={"primary"} onClick={() => onClickDetails(props.row.original)}>
                        {resources.tables.verificationTransactionDetailsScores.columns.details.button.label}
                    </Button>
                </div>
            )
        })
    ]), [scoreTableData]);

    const useTableResult = useReactTable({
        columns,
        data: scoreTableData,
        filterFns: null,
        enableSorting: false,
        enableFilters: false,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="verification-transaction-scores"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"verification-transaction-scores"}
            />
        </IdwTableContainer>
    );
};